<template>
  <!--
    내선 관리 › 전화기 sub-tab. 위: 미배정 전화기(설정 서버에 접속했지만 내선에 안 붙은 MAC) 표,
    아래: 연결된 전화기 master-detail(번호 관리 AgentPhoneNumbersView 와 같은 320px 목록 + 상세).
    목록은 호스트(ExtensionManage)가 받은 inventory 를 쓰고, 상태 필터는 클라이언트에서 한다.
    계약: ~/md/front/studio-내선·전화기·사이트-탭-요청.md "화면 명세 › 전화기"
  -->
  <div class="pbx-devices">
    <div v-if="loadFailed" class="pbx-devices__card pbx-devices__state pbx-devices__state--error">
      <p>전화기 목록을 불러오지 못했습니다.</p>
      <button type="button" class="pbx-devices__ghost" @click="retry">다시 시도</button>
    </div>

    <template v-else>
      <!-- 재조회 실패 — 기존 목록은 남기고 다시 시도만 안내 -->
      <p v-if="reloadFailed" class="pbx-devices__banner" role="alert">
        전화기 목록을 불러오지 못했습니다.
        <button type="button" class="pbx-devices__link" @click="retry">다시 시도</button>
      </p>

      <!-- 미배정 전화기 -->
      <section class="pbx-devices__card pbx-devices__unassigned">
        <header class="pbx-devices__card-header">
          <div class="pbx-devices__card-heading">
            <h2 class="pbx-devices__title">
              미배정 전화기
              <span v-if="!firstLoading" class="pbx-devices__count">{{ unassigned.length }}</span>
            </h2>
            <p class="pbx-devices__desc">설정 서버에 접속했지만 아직 어느 내선에도 연결되지 않은 전화기입니다.</p>
          </div>
          <button type="button" class="pbx-devices__add" :disabled="firstLoading" @click="registerOpen = true">
            <PlusOutlined /> 전화기 등록
          </button>
        </header>

        <a-skeleton v-if="firstLoading" active :title="false" :paragraph="{ rows: 2 }" class="pbx-devices__skeleton" />
        <p v-else-if="!unassigned.length" class="pbx-devices__empty-text">
          설정 서버에 접속한 미배정 전화기가 없습니다. 설치 장소의 프로비저닝 URL 을 전화기 Server Address 에 넣고
          재부팅하면 여기에 나타납니다. MAC 을 알면 [전화기 등록]으로 미리 넣을 수 있습니다.
        </p>
        <div v-else class="pbx-devices__table-wrap">
          <table class="pbx-devices__table">
            <thead>
              <tr>
                <th>MAC</th>
                <th>모델</th>
                <th>첫 접속</th>
                <th>접속 IP</th>
                <th>UA</th>
                <th>설치 장소</th>
                <th aria-label="작업"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="d in unassigned"
                :key="d.id"
                class="pbx-devices__row"
                :class="{ 'is-active': d.id === selectedId }"
                @click="select(d.id)"
              >
                <td>
                  <span class="pbx-devices__mono">{{ d.mac }}</span>
                  <span v-if="d.label" class="pbx-devices__row-label">{{ d.label }}</span>
                </td>
                <td>{{ d.model ?? '—' }}</td>
                <td :title="relative(d.first_seen?.at)">{{ formatPbxTime(d.first_seen?.at) }}</td>
                <td>{{ d.first_seen?.ip_masked ?? '—' }}</td>
                <td class="pbx-devices__ua" :title="deviceUserAgent(d)">{{ deviceUserAgent(d) }}</td>
                <td>{{ siteName(d.site_id) }}</td>
                <td class="pbx-devices__cell-action">
                  <button type="button" class="pbx-devices__primary" @click.stop="openClaim(d)">연결</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- 연결된 전화기 master-detail -->
      <div class="pbx-devices__body">
        <section class="pbx-devices__card pbx-devices__list">
          <header class="pbx-devices__list-header">
            <h2 class="pbx-devices__title pbx-devices__title--grow">
              연결된 전화기
              <span v-if="!firstLoading" class="pbx-devices__count">{{ assigned.length }}</span>
            </h2>
            <a-checkbox v-model:checked="showReturned" class="pbx-devices__filter">반납 포함</a-checkbox>
            <button
              type="button"
              class="pbx-devices__mini"
              title="새로고침"
              :disabled="inventory.devices.loading.value"
              @click="retry"
            >
              <ReloadOutlined />
            </button>
          </header>

          <a-skeleton v-if="firstLoading" active :title="false" :paragraph="{ rows: 5 }" class="pbx-devices__skeleton" />
          <div v-else-if="!connected.length" class="pbx-devices__state">
            <p>
              연결된 전화기가 없습니다. 위 미배정 목록에서 [연결]을 눌러 상담사 번호의 내선에 전화기를
              붙이세요.
            </p>
          </div>
          <div v-else class="pbx-devices__items">
            <p v-if="onlyReturned" class="pbx-devices__items-note">반납된 전화기만 있습니다.</p>
            <button
              v-for="d in connected"
              :key="d.id"
              type="button"
              class="pbx-devices__item"
              :class="{ 'is-active': d.id === selectedId, 'is-returned': d.status === 'returned' }"
              @click="select(d.id)"
            >
              <span class="pbx-devices__item-name">{{ d.label || d.mac }}</span>
              <span v-if="d.label" class="pbx-devices__item-raw">{{ d.mac }}</span>
              <span class="pbx-devices__item-meta">
                <span v-if="extensionNumberOf(d)" class="pbx-devices__item-ext">내선 {{ extensionNumberOf(d) }}</span>
                <PbxBadge v-if="d.status === 'returned'" :badge="deviceStatusBadge(d.status)" />
                <template v-else>
                  <PbxBadge :badge="provisioningStateBadge(d.provisioning_state)" />
                  <PbxBadge :badge="transportOf(d)" />
                </template>
              </span>
              <span class="pbx-devices__item-sub">{{ counselorNameOf(d) }}</span>
            </button>
          </div>
        </section>

        <section class="pbx-devices__card pbx-devices__detail">
          <PbxDeviceDetail
            v-if="selected"
            :inventory="inventory"
            :device="selected"
            :show-claim-hint="claimHintId === selected.id && selected.status === 'assigned'"
            @claim="openClaim"
            @returned="onReturned"
          />
          <div v-else class="pbx-devices__placeholder">
            <MobileOutlined class="pbx-devices__placeholder-icon" />
            <p>전화기를 선택하세요</p>
            <small>연결·프로비저닝·전송·관리자 비밀번호·복구 코드·로그를 관리할 수 있습니다.</small>
          </div>
        </section>
      </div>
    </template>

    <PbxDeviceRegisterModal v-model:open="registerOpen" :inventory="inventory" @created="onCreated" />
    <PbxDeviceClaimModal
      v-model:open="claimOpen"
      :inventory="inventory"
      :device="claimTarget"
      @claimed="onClaimed"
    />
  </div>
</template>

<script setup>
import { MobileOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons-vue';
import { computed, ref } from 'vue';

import {
  deviceStatusBadge,
  formatPbxRelative,
  formatPbxTime,
  provisioningStateBadge,
  transportBadge,
  compareLabel,
} from '@/views/Callbot/service/pbxExtensions';

import PbxBadge from './PbxBadge.vue';
import PbxDeviceClaimModal from './PbxDeviceClaimModal.vue';
import PbxDeviceDetail from './PbxDeviceDetail.vue';
import PbxDeviceRegisterModal from './PbxDeviceRegisterModal.vue';
import { deviceName, deviceUserAgent } from './PbxDeviceShared';

const props = defineProps({ inventory: { type: Object, required: true } });

// --- 로딩·실패 ---------------------------------------------------------------

// stage7 null = 판정 전(설치 장소 프로브 중). 판정 뒤에도 전화기 목록을 한 번도 못 받았으면 첫 로딩
const firstLoading = computed(() => {
  const devices = props.inventory.devices;
  return props.inventory.stage7.value === null || (!devices.loaded.value && !devices.error.value);
});
// 프로브가 실패하면 판정이 null 에 멈춰 전화기를 부르지도 않는다 — 스켈레톤 대신 실패로 보인다
const loadFailed = computed(() => {
  const devices = props.inventory.devices;
  return props.inventory.stage7ProbeFailed.value || (devices.error.value && !devices.loaded.value);
});
const reloadFailed = computed(() => props.inventory.devices.error.value && props.inventory.devices.loaded.value);

function retry() {
  // 판정 전이면 전화기 재조회는 아무것도 안 한다 — 프로브부터 다시(성공하면 전화기까지 받는다)
  if (props.inventory.stage7.value === null) void props.inventory.reprobe();
  else void props.inventory.reload(['devices']);
}

// --- 목록 --------------------------------------------------------------------

// 서버는 최신순으로 준다 — 화면은 이름(라벨, 없으면 MAC)순이 찾기 쉽다
const byStatus = (status) =>
  props.inventory.devices.items.value
    .filter((d) => d.status === status)
    .sort((a, b) => compareLabel(deviceName(a), deviceName(b)));
const unassigned = computed(() => byStatus('unassigned'));
const assigned = computed(() => byStatus('assigned'));
const returned = computed(() => byStatus('returned'));

// 반납은 기본 숨김 — 필터를 켜면 연결 목록 아래에 회색으로
const showReturned = ref(false);
const connected = computed(() => (showReturned.value ? [...assigned.value, ...returned.value] : assigned.value));
const onlyReturned = computed(() => showReturned.value && !assigned.value.length && returned.value.length > 0);

function relative(ms) {
  return ms == null ? undefined : formatPbxRelative(ms, props.inventory.now.value);
}

function siteName(siteId) {
  return props.inventory.siteById.value.get(siteId)?.name ?? '—';
}

function extensionNumberOf(d) {
  if (d.extension_id) {
    const ext = props.inventory.extensionById.value.get(d.extension_id);
    if (ext) return ext.number;
  }
  if (d.counselor_number_id) {
    return props.inventory.counselorNumberById.value.get(d.counselor_number_id)?.pbx_extension?.number ?? null;
  }
  return null;
}

function counselorNameOf(d) {
  if (!d.counselor_number_id) return '상담사 번호 없음';
  return props.inventory.counselorNumberById.value.get(d.counselor_number_id)?.name ?? '—';
}

function transportOf(d) {
  const site = props.inventory.siteById.value.get(d.site_id);
  return transportBadge(
    d.sip_transport,
    d.sip_port,
    site ? { transport: site.sip_transport_default, port: site.sip_port_default } : null
  );
}

// --- 선택 --------------------------------------------------------------------

// 미배정 행도 열 수 있다(복구 코드·반납·라벨). 재조회로 사라진 전화기면 빈 상세로 돌아간다
const selectedId = ref(null);
const selected = computed(() =>
  selectedId.value ? props.inventory.deviceById.value.get(selectedId.value) ?? null : null
);
// 방금 [연결]한 전화기 — 다른 전화기를 고르기 전까지 첫 설정 수신 안내를 보인다
const claimHintId = ref(null);

function select(id) {
  if (selectedId.value !== id) claimHintId.value = null;
  selectedId.value = id;
}

function onReturned(id) {
  // 반납 행은 기본 숨김 — 필터가 꺼져 있으면 상세도 닫는다
  if (!showReturned.value && selectedId.value === id) selectedId.value = null;
}

// --- 등록·연결 모달 ----------------------------------------------------------

const registerOpen = ref(false);

function onCreated(d) {
  select(d.id);
}

const claimOpen = ref(false);
const claimTarget = ref(null);

function openClaim(d) {
  claimTarget.value = d;
  claimOpen.value = true;
}

function onClaimed(d) {
  selectedId.value = d.id;
  claimHintId.value = d.id;
}
</script>

<style scoped lang="scss">
.pbx-devices {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  // 화면이 낮으면 패널 전체가 세로로 스크롤한다(가로 스크롤은 표 카드 안에서만)
  overflow-y: auto;
  font-size: 13px;

  &__card {
    min-width: 0;
    border: 1px solid #f0f0f0;
    border-radius: 12px;
    background: #fff;
  }

  &__banner {
    flex: none;
    margin: 0;
    padding: 8px 14px;
    border: 1px solid #ffccc7;
    border-radius: 10px;
    background: #fff2f0;
    color: #cf1322;
  }

  // 미배정 전화기
  &__unassigned {
    flex: none;
    display: flex;
    flex-direction: column;
  }

  &__card-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 14px;
    border-bottom: 1px solid #f0f0f0;
  }

  &__card-heading {
    min-width: 0;
  }

  &__title {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: #262626;

    &--grow {
      flex: 1;
      min-width: 0;
    }
  }

  &__count {
    padding: 0 7px;
    border-radius: 999px;
    background: #f5f5f5;
    color: #8c8c8c;
    font-size: 11px;
    font-weight: 600;
    line-height: 18px;
  }

  &__desc {
    margin: 2px 0 0;
    font-size: 12px;
    color: #8c8c8c;
  }

  &__add {
    flex: none;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 14px;
    border: 1px solid #1668dc;
    border-radius: 10px;
    background: #1668dc;
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;

    &:hover:not(:disabled) {
      background: #0f5bc4;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  &__skeleton {
    padding: 14px;
  }

  &__empty-text {
    margin: 0;
    padding: 20px 16px;
    color: #8c8c8c;
    line-height: 1.6;
  }

  &__table-wrap {
    max-height: 240px;
    overflow: auto;
  }

  &__table {
    width: 100%;
    border-collapse: collapse;

    th {
      position: sticky;
      top: 0;
      z-index: 1;
      padding: 9px 14px;
      background: #fafafa;
      border-bottom: 1px solid #f0f0f0;
      color: #8c8c8c;
      font-size: 12px;
      font-weight: 600;
      text-align: left;
      white-space: nowrap;
    }

    td {
      padding: 9px 14px;
      border-bottom: 1px solid #fafafa;
      color: #262626;
      white-space: nowrap;
    }
  }

  &__row {
    cursor: pointer;

    &:hover td {
      background: #fafafa;
    }

    &.is-active td {
      background: #f0f5ff;
    }
  }

  &__row-label {
    margin-left: 8px;
    color: #8c8c8c;
    font-size: 12px;
  }

  &__mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  }

  &__ua {
    max-width: 260px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__cell-action {
    text-align: right;
  }

  &__primary {
    padding: 4px 14px;
    border: 1px solid #1668dc;
    border-radius: 8px;
    background: #fff;
    color: #1668dc;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
      background: #f0f5ff;
    }
  }

  // 연결된 전화기 master-detail
  &__body {
    flex: 1;
    min-height: 420px;
    display: grid;
    grid-template-columns: 320px minmax(0, 1fr);
    gap: 16px;
  }

  &__list {
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
  }

  &__list-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 14px;
    border-bottom: 1px solid #f0f0f0;
  }

  &__filter {
    flex: none;
    font-size: 12px;
  }

  &__items {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__items-note {
    margin: 4px 4px 6px;
    color: #8c8c8c;
    font-size: 12px;
  }

  &__item {
    display: flex;
    flex-direction: column;
    gap: 4px;
    height: auto; // 전역 button { height: 40px } 덮어쓰기 — 없으면 메타/상담사 줄이 다음 항목과 겹침
    padding: 10px 12px;
    border: none;
    border-radius: 8px;
    background: transparent;
    text-align: left;
    cursor: pointer;

    &:hover {
      background: #fafafa;
    }

    &.is-active {
      background: #f0f5ff;
    }

    // 반납된 전화기 — 필터로만 보이는 이력
    &.is-returned {
      opacity: 0.55;
    }

    &-name {
      font-size: 14px;
      font-weight: 600;
      color: #262626;
      word-break: break-all;
    }

    &-raw {
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
      font-size: 12px;
      color: #8c8c8c;
    }

    &-meta {
      display: flex;
      align-items: center;
      gap: 6px;
      flex-wrap: wrap;
    }

    &-ext {
      font-size: 12px;
      font-weight: 600;
      color: #595959;
      font-variant-numeric: tabular-nums;
    }

    &-sub {
      font-size: 12px;
      color: #8c8c8c;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &__detail {
    min-height: 0;
    overflow-y: auto;
    padding: 24px;
  }

  &__placeholder {
    height: 100%;
    min-height: 240px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    text-align: center;

    &-icon {
      font-size: 40px;
      color: #d9d9d9;
    }

    p {
      margin: 4px 0 0;
      font-size: 16px;
      font-weight: 600;
      color: #262626;
    }

    small {
      max-width: 380px;
      font-size: 13px;
      color: #8c8c8c;
      line-height: 1.6;
    }
  }

  &__state {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 40px 16px;
    color: #8c8c8c;
    text-align: center;
    line-height: 1.6;

    p {
      margin: 0;
    }

    &--error {
      color: #cf1322;
    }
  }

  &__ghost {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    background: #fff;
    color: #595959;
    font-size: 13px;
    cursor: pointer;

    &:hover {
      border-color: #bfbfbf;
    }
  }

  &__link {
    padding: 0;
    margin-left: 6px;
    border: none;
    background: none;
    color: #1668dc;
    font-size: 13px;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  &__mini {
    flex: none;
    width: 28px;
    height: 28px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    background: #fff;
    color: #595959;
    cursor: pointer;

    &:hover:not(:disabled) {
      border-color: #bfbfbf;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}

@media (max-width: 1024px) {
  .pbx-devices__body {
    grid-template-columns: 1fr;
  }
}
</style>
